import React, { useCallback, useEffect } from "react";
import { GuiButton } from "../components/global-ui/gui-button";
import { Icon } from "../components/icon";
import PageLayout from "../components/page-layout";
import { useSelector } from "react-redux";
import Grid, { ColumnHeader } from "../components/grid";
import Card from "../components/card";
import {
  CheckBoxHeaderButton,
  GuiCheckbox,
} from "../components/global-ui/gui-checkbox";
import { downloadFile, useUpdateState } from "../utilities/utilities";
import { GridCell } from "../components/grid";
import Popover from "../components/popover";
import "../styles/global-ui/group-members.scss";
import { SearchBox } from "../components/search-box";
import { FilterDropdown } from "../components/filter-dropdown";
import actions from "../actions/actions";
import { EditMultiple } from "../components/edit-multiple";
import { ColumnPicker } from "../components/column-picker";
import { FilterTagBar } from "../components/filter-tag-bar";
import { storage } from "../utilities/storage";
import { dispatch } from "../redux/redux-config";
import { Modal } from "../components/modal";
import { GuiSelect } from "../components/global-ui/gui-select";
import { useState } from "react";
import { showToast } from "../components/toast";
import { Path, UserRole } from "../utilities/constants";
import navigate from "../utilities/navigation";

export default function GroupMembers({ match, location }) {
  const currentUser = useSelector((store) => store.user);
  const userRole = currentUser?.userType;
  let [state, updateState] = useUpdateState(() => {
    const editMultipleItems = [
      { name: "Add Users to Group" },
      ...(userRole === UserRole.Admin ? [{ name: "Edit" }] : []),
      // Only show delete option for Admin
      ...(userRole === UserRole.Admin
        ? [{ name: "Delete Users from Group" }]
        : []),
      /*{ name: "Send Alert to Users" },*/
      // { name: "Make High Priority" },
      // { name: "Remove High Priority" },
      { name: "Export" },
    ];
    return {
      editMultipleItems,
      criteriaOptions: [
        { value: "no-login", label: "Never Logged In" },
        { value: "no-email", label: "No Email" },
        { value: "no-cell", label: "No Cell Number" },
        { value: "no-phone", label: "No Phone Number" },
        { value: "no-phone-or-email", label: "No Phone Number or Email" },
        { value: "one-phone", label: "Only One Phone Number" },
        { value: "has-phone", label: "Has a Phone Number" },
        { value: "no-import-delete", label: "Do Not Delete On Import" },
        { value: "alert-permission", label: "Has Message Permissions" },
        { value: "opted-out-sms", label: "Opted Out of Text Messages" },
      ],
      search: "",
      filters: [],
      sort: {},
      sortOrder: [],
      gridReset: 0,
      currentPage: [],
      showActionsMenu: null,
      defaultGridColumns: [],
    };
  });

  let [checkboxState, updateCheckboxState, setCheckboxState] = useUpdateState(
    {},
  );
  let { gridColumns } = useSelector((store) => store.groups);

  const handleKeyDown = (e) => {};

  useEffect(() => {
    actions.groups.initPage();
    if (!gridColumns) {
      const defaultGridColumns = [
        { name: "Group Name", column: "groupname", show: true },
        { name: "Location", column: "location", show: true },
        { name: "Type", column: "grouptype", show: true },
        { name: "Alerts", column: "alerts", show: true },
        { name: "Group ID", column: "groupid", show: false },
      ];
      updateState({ defaultGridColumns: defaultGridColumns });
    }
  }, []);

  let groupId = match.params.id;
  let { groupDetails, groupName: initialGroupName } = location.state || {};
  const [groupName, setGroupName] = useState(initialGroupName);
  const routeState = location?.state || {};
  const [groupDetailsState, setGroupDetailsState] = useState(
    routeState.groupDetails || null,
  );
  const [groupNameState, setGroupNameState] = useState(
    routeState.groupName || "",
  );

  let isMobile = useSelector((store) => store.page.isMobile);

  let userTypeOptions =
    useSelector((store) => actions.user.loadUserTypes()) || [];

  let selectedRows = Object.keys(checkboxState).filter((k) => checkboxState[k]);

  let groupOptions =
    useSelector((store) => actions.groups.loadGroupOptions()) || [];
  const groupNameFromOptions =
    groupOptions.find(
      (g) =>
        String(g.value) === String(groupId) ||
        String(g.id) === String(groupId) ||
        String(g.groupId) === String(groupId),
    )?.label || "";
  const [showPopover, setShowPopover] = useState(false);
  const [showModal, setShowModal] = useState({ show: false, userId: null });
  const [showRemoveModal, setShowRemoveModal] = useState({
    show: false,
    userId: null,
  });

  const columnKeyMap = {
    groupname: "firstName",
    location: "lastName",
    grouptype: "userName",
    groupid: "userType",
  };

  const getMembers = useCallback((details) => {
    if (!details) return [];
    if (Array.isArray(details.members)) return details.members;
    if (Array.isArray(details.list)) return details.list;
    if (Array.isArray(details.data?.members)) return details.data.members;
    if (Array.isArray(details.data?.list)) return details.data.list;
    return [];
  }, []);

  const normalizeGroupDetails = useCallback(
    (details) => {
      if (!details) return null;
      return {
        ...details,
        members: getMembers(details),
      };
    },
    [getMembers],
  );

  const isMeaningfulGroupName = useCallback((name) => {
    if (!name || typeof name !== "string") return false;
    const trimmed = name.trim();
    return trimmed.length > 0 && trimmed !== "Group Members";
  }, []);

  React.useEffect(() => {}, [groupDetailsState]);

  useEffect(() => {
    async function ensureGroupContext() {
      if (groupDetailsState) return;

      const details = await actions.groups.getAllGroupMembers(groupId);
      if (details) {
        setGroupDetailsState(normalizeGroupDetails(details));
        const fetchedName =
          details.groupName || details.name || details.group?.name || "";
        if (!groupNameState && isMeaningfulGroupName(fetchedName)) {
          setGroupNameState(fetchedName);
        }
      }
    }

    ensureGroupContext();
  }, [
    groupDetailsState,
    groupId,
    groupNameState,
    normalizeGroupDetails,
    isMeaningfulGroupName,
  ]);

  useEffect(() => {
    async function ensureGroupName() {
      if (
        isMeaningfulGroupName(groupNameState) ||
        isMeaningfulGroupName(groupNameFromOptions)
      )
        return;

      const detailsResult = await actions.groups.getGroupDetails(groupId);
      const fetchedName =
        detailsResult?.group?.name ||
        detailsResult?.groupName ||
        detailsResult?.name ||
        "";

      if (isMeaningfulGroupName(fetchedName)) setGroupNameState(fetchedName);
    }

    ensureGroupName();
  }, [groupId, groupNameFromOptions, groupNameState, isMeaningfulGroupName]);

  function toggleAllCheckboxes() {
    // Get all userIds for the current page
    const allIds = state.currentPage.map((row) => row.userId);
    // Check if all are already selected
    const allChecked = allIds.every((id) => checkboxState[id]);
    if (allChecked) {
      // Uncheck all
      setCheckboxState({});
    } else {
      // Check all
      const newState = {};
      allIds.forEach((id) => {
        newState[id] = true;
      });
      setCheckboxState(newState);
    }
  }

  let { sort } = state;

  let filterOptions = [
    { name: "User Type", options: userTypeOptions || [] },
    { name: "Criteria", options: state.criteriaOptions || [] },
  ];

  function onSort(column) {
    let currentDirection = state.sort[column];
    let nextDirection =
      currentDirection === true
        ? false
        : currentDirection === false
          ? undefined
          : true;

    // Only keep sort for the clicked column
    let newSort = {};
    let newSortOrder = [];
    if (nextDirection !== undefined) {
      newSort[column] = nextDirection;
      newSortOrder = [column];
    }

    updateState({
      sort: newSort,
      sortOrder: newSortOrder,
      gridReset: state.gridReset + 1,
    });
  }

  function setSearch(search) {
    updateState({ search: search, gridReset: state.gridReset + 1 });
  }

  function onFilterChange(action, values) {
    if (action === "clear") {
      updateState({
        filters: [],
        gridReset: state.gridReset + 1,
        currentPageNumber: 1,
      });
    } else {
      // Remove duplicates and accumulate user types
      let newFilters = [...state.filters];
      values.forEach((val) => {
        // Only add if not already present
        if (
          !newFilters.some((f) => f.name === val.name && f.value === val.value)
        ) {
          newFilters.push(val);
        }
      });
      updateState({
        filters: newFilters,
        gridReset: state.gridReset + 1,
        currentPageNumber: 1,
      });
    }
  }

  function goToUserDashboard(member) {
    if (!member?.userId) return;

    localStorage.setItem(
      "selectedPerson",
      JSON.stringify({
        id: member?.userId,
        name: member?.firstName || "",
        userTypeId: Number(member?.userType) || null,
      }),
    );

    navigate(Path.PeopleDashboard.replace(":id", member?.userId));
  }

  async function onEditMultipleClick(item) {
    if (item.name === "Add Users to Group")
      updateState({ showAddToGroupModal: true, individual: null });
    else if (item.name === "Edit") {
      let selectedUserIds = Object.keys(checkboxState).filter(
        (k) => checkboxState[k],
      );

      if (selectedUserIds.length !== 1) {
        showToast("error", "Please select exactly one user to edit.");
        return;
      }

      const selectedId = Number(selectedUserIds[0]);
      const member = (groupDetailsState?.members || []).find(
        (m) => Number(m.userId) === selectedId,
      );
      if (!member) {
        showToast("error", "Unable to open selected user.");
        return;
      }

      goToUserDashboard(member);
    } else if (item.name === "Delete Users from Group")
      updateState({ showDeleteUsersModal: true, individual: null });
    else if (item.name === "Make High Priority")
      updateState({ showMakeHighPriority: true, individual: null });
    else if (item.name === "Remove High Priority")
      updateState({ showRemoveHighPriority: true, individual: null });
    else if (item.name === "Export") {
      let token = await actions.user.getShortTermToken();
      if (token) {
        downloadFile(
          "/api/people/export?personIds=" +
            Object.keys(checkboxState)
              .filter((k) => checkboxState[k])
              .join(",") +
            "&token=" +
            encodeURIComponent(token),
        );
      }
    }
  }

  async function addUsersToGroup() {
    let selectedUserIds = Object.keys(checkboxState).filter(
      (k) => checkboxState[k],
    );
    let userIds =
      selectedUserIds.length > 0
        ? selectedUserIds
        : state?.individual
          ? [state?.individual?.id]
          : [];
    updateState({ saving: true });
    const result = await actions.people.addPeopleToGroup(
      state.addToGroupId,
      userIds,
    );
    if (result?.success) {
      showToast("success", "User added to group successfully!");
      setCheckboxState({});
      updateState({
        saving: false,
        showAddToGroupModal: false,
        addToGroupId: null,
        individual: null,
        gridReset: state.gridReset + 1,
      });
    } else {
      const errorMsg =
        result?.errorMessage && result.errorMessage.trim().length > 0
          ? result.errorMessage
          : "Failed to add user to group.";
      showToast("error", errorMsg);
      updateState({ saving: false });
    }
  }

  async function deleteUsers() {
    let userIds = state.individual
      ? [state.individual.id]
      : Object.keys(checkboxState).filter((k) => checkboxState[k]);
    updateState({ saving: true });

    var result = await actions.groups.deleteUsersFromGroup(groupId, userIds);

    if (result && result.success) {
      showToast("success", "User(s) deleted successfully");
    }

    // Fetch the updated group details
    let details = await actions.groups.getAllGroupMembers(groupId);

    setGroupDetailsState(normalizeGroupDetails(details));
    // Reset checkboxes and grid
    setCheckboxState({});

    // Reset the grid to reflect changes
    updateState({
      saving: false,
      showDeleteUsersModal: false,
      individual: null,
      gridReset: state.gridReset + 1,
    });
  }

  async function setUserPriority(setHigh) {
    let userIds = state.individual
      ? [state.individual.id]
      : Object.keys(checkboxState).filter((k) => checkboxState[k]);
    updateState({ saving: true });
    await actions.people.setPriority(userIds, setHigh);
    updateState({
      saving: false,
      showMakeHighPriority: false,
      showRemoveHighPriority: false,
      individual: null,
      gridReset: state.gridReset + 1,
    });
  }

  function onColumnChange(item, show) {
    let cols = [...gridColumns];
    let i = cols.findIndex((c) => c.name === item.name);
    cols[i] = { ...cols[i], show: show };

    let list = cols.filter((c) => c.show).map((c) => c.column);
    storage.setObject("groups-columns", list);

    dispatch("GROUP_SETTINGS_UPDATE", { gridColumns: cols });
  }

  function removeFilter(filter) {
    updateState({
      filters: state.filters.filter((f) => f !== filter),
      gridReset: state.gridReset + 1,
    });
  }

  function getUsersText() {
    return state.individual
      ? state.individual.firstName + " " + state.individual.lastName
      : "these users";
  }

  async function onItemsNeeded(page, pageSize, skipTotal) {
    let groupDetails = groupDetailsState;
    let members = getMembers(groupDetails);

    if ((!members || members.length === 0) && groupId) {
      const details = await actions.groups.getAllGroupMembers(groupId);
      const normalizedDetails = normalizeGroupDetails(details);
      if (normalizedDetails) {
        setGroupDetailsState(normalizedDetails);
        members = getMembers(normalizedDetails);

        if (!groupNameState) {
          const fetchedName =
            details?.groupName || details?.name || details?.group?.name || "";
          if (isMeaningfulGroupName(fetchedName)) {
            setGroupNameState(fetchedName);
          }
        }
      }
    }

    if (Array.isArray(members)) {
      let items = members;

      // Filter items based on search state
      if (state.search.trim()) {
        items = items.filter((item) => {
          return (
            item.firstName
              .toLowerCase()
              .includes(state.search.trim().toLowerCase()) ||
            item.lastName
              .toLowerCase()
              .includes(state.search.trim().toLowerCase())
          );
        });
      }

      // --- Apply dropdown filters ---
      if (state.filters && state.filters.length > 0) {
        state.filters.forEach((filter) => {
          const userTypeFilters = state.filters
            .filter((f) => f.name === "User Type" && f.value)
            .map((f) => f.value);

          if (userTypeFilters.length > 0) {
            items = items?.filter((item) =>
              userTypeFilters.includes(item.userType),
            );
          }

          const criteriaFilters = state?.filters
            .filter((f) => f.name === "Criteria" && f.value)
            .map((f) => f.value);

          if (criteriaFilters.length > 0) {
            items = items?.filter(
              (item) =>
                Array.isArray(item.criteria) &&
                criteriaFilters.some((filterValue) =>
                  item.criteria.includes(filterValue),
                ),
            );
          }
        });
      }
      // --- End dropdown filters ---

      // --- Add sorting logic here ---
      const sortOrder = state.sortOrder;
      if (sortOrder.length > 0) {
        items = items.slice().sort((a, b) => {
          for (let i = 0; i < sortOrder.length; i++) {
            const sortCol = sortOrder[i];
            const asc = state.sort[sortCol];
            const dataKey = columnKeyMap[sortCol] || sortCol;
            if (asc === undefined) continue; // skip columns not sorted
            if (a[dataKey] < b[dataKey]) return asc ? -1 : 1;
            if (a[dataKey] > b[dataKey]) return asc ? 1 : -1;
            // If equal, continue to next sort key
          }
          return 0;
        });
      }
      // --- End sorting logic ---
      const filteredCount = items.length;
      items = items.slice((page - 1) * pageSize, page * pageSize);
      updateState({ currentPage: items });
      return {
        list: items,
        totalCount: filteredCount,
      };
    } else {
      return {
        list: [],
        totalCount: 0,
      };
    }
  }

  function getUserType(userType) {
    switch (Number(userType)) {
      case 1:
        return "Student";
      case 2:
        return "";
      case 3:
        return "Faculty";
      case 4:
        return "Admin";
      default:
        return "";
    }
  }

  function handleCheckboxChange(userId, checked) {
    updateCheckboxState(userId, checked);
  }

  const handlePopoverHide = (id) => {
    setShowPopover(id);
  };

  async function handleMakeLeader(userId) {
    updateState({ saving: true });
    await actions.groups.handleMakeLeader(userId);
    setShowModal({ show: false, userId: null });
    updateState({ saving: false });
    handlePopoverHide(null);
  }

  async function handleRemove(userId, groupId) {
    try {
      updateState({ ...state, saving: true });
      var result = await actions.groups.removePeopleFromGroup(groupId, userId);
      if (result && result.success) {
        showToast("success", "User deleted successfully");
      }
      setShowRemoveModal({ show: false, userId: null });
      // Reset checkboxes and grid
      setCheckboxState({});
      updateState({ ...state, saving: false });
      selectedRows = [];
      handlePopoverHide(null);
      // Fetch the updated group details
      let details = await actions.groups.getAllGroupMembers(groupId);

      // Check if the removed user is still in the details.members array
      if (getMembers(details).find((member) => member.userId === userId)) {
        // The removed user is still in the fetched group details
      }

      // Update the state with the new group details

      setGroupDetailsState(normalizeGroupDetails(details));
    } catch (error) {
      console.error("Error in handleRemove:", error);
    }
    // window.location.reload();
  }

  const resolvedGroupName =
    (isMeaningfulGroupName(groupNameState) ? groupNameState : "") ||
    (isMeaningfulGroupName(groupDetailsState?.groupName)
      ? groupDetailsState?.groupName
      : "") ||
    (isMeaningfulGroupName(groupDetailsState?.name)
      ? groupDetailsState?.name
      : "") ||
    (isMeaningfulGroupName(groupDetailsState?.group?.name)
      ? groupDetailsState?.group?.name
      : "") ||
    (isMeaningfulGroupName(groupNameFromOptions) ? groupNameFromOptions : "") ||
    "Group Members";

  return (
    <PageLayout
      title={resolvedGroupName}
      className="group-members"
      backbutton={true}
      onBackClick={() => navigate(Path.Groups)}
      backButtonLabel="Groups"
      buttonBar={
        <>
          <GuiButton
            onClick={() => {
              const groupObj = {
                id: groupId,
                name: resolvedGroupName,
              };
              const group = [groupObj];
              actions.alerts.showCreateAlert(false, group);
            }}
            className="create-group-button"
          >
            Message Group
          </GuiButton>
        </>
      }
      searching={false}
    >
      <Card className="group-members-card">
        {/* Desktop Search bar */}
        {!isMobile && (
          <div className="page-layout-content-header">
            <SearchBox onSearchChange={setSearch} searching={state.loading} />
            <FilterDropdown
              filters={filterOptions}
              onFilterChange={onFilterChange}
            />

            <div className="tools">
              <EditMultiple
                items={state.editMultipleItems}
                onItemClick={onEditMultipleClick}
                count={selectedRows.length}
              />
              <ColumnPicker
                items={gridColumns ? gridColumns : state.defaultGridColumns}
                onItemChange={onColumnChange}
              />
            </div>

            <FilterTagBar filters={state.filters} onRemove={removeFilter} />
          </div>
        )}

        {/* Mobile filter bar */}
        {isMobile && (
          <div className="mobile-filter-bar">
            <FilterDropdown
              filters={filterOptions}
              onFilterChange={onFilterChange}
            />
          </div>
        )}
        <Grid
          // groupDetails={groupDetails}
          groupDetails={groupDetailsState}
          header={
            <div className="header-row row">
              <ColumnHeader className="check">
                <CheckBoxHeaderButton
                  checked={Object.keys(checkboxState).find(
                    (k) => checkboxState[k],
                  )}
                  onClick={toggleAllCheckboxes}
                />
              </ColumnHeader>
              <ColumnHeader className="groupname" sort={sort} onSort={onSort}>
                First Name
              </ColumnHeader>
              <ColumnHeader className="location" sort={sort} onSort={onSort}>
                Last Name
              </ColumnHeader>
              <ColumnHeader className="grouptype" sort={sort} onSort={onSort}>
                User Name
              </ColumnHeader>
              <ColumnHeader className="groupid" sort={sort} onSort={onSort}>
                User Type
              </ColumnHeader>
            </div>
          }
          itemTemplate={(g) => {
            return (
              <div className="row" key={g?.userId}>
                <GridCell className="check">
                  <GuiCheckbox
                    name={"" + g?.userId}
                    value={checkboxState[g?.userId]}
                    onChange={(name, checked) =>
                      handleCheckboxChange(name, checked)
                    }
                  />
                </GridCell>
                <GridCell className="grid-initials">
                  <span className="initials">
                    {(g.firstName || "").substr(0, 2)}
                  </span>
                </GridCell>
                <GridCell className="location">{g?.firstName || ""}</GridCell>
                <GridCell className="grouptype">{g?.lastName || ""}</GridCell>
                <GridCell className="groupid">{g?.userName || ""}</GridCell>
                <GridCell className="alerts">
                  {getUserType(g?.userType) || ""}
                </GridCell>

                {userRole === UserRole.Admin ? (
                  <GridCell className="actions">
                    <Icon
                      icon="ellipsis-h"
                      weight="400"
                      onClick={() => {
                        handlePopoverHide(g.userId);
                      }}
                    />
                    <Popover
                      show={
                        showPopover === g.userId &&
                        !showModal.show &&
                        !showRemoveModal.show
                      }
                      onHide={() => {
                        handlePopoverHide(null);
                      }}
                      className="make-leader-div"
                    >
                      {/* <div
                      className="menu-item"
                      onClick={() =>
                        setShowModal({ show: true, userId: g.userId })
                      }
                    >
                      Make Leader
                    </div> */}
                      <div
                        className="menu-item"
                        onClick={() => goToUserDashboard(g)}
                      >
                        Edit
                      </div>
                      <div
                        className="menu-item"
                        onClick={() =>
                          setShowRemoveModal({ show: true, userId: g.userId })
                        }
                      >
                        Remove
                      </div>
                    </Popover>
                  </GridCell>
                ) : (
                  <GridCell className="actions"></GridCell>
                )}
              </div>
            );
          }}
          onItemsNeeded={onItemsNeeded}
          search={state.search.trim()}
          gridReset={state.gridReset}
        />
      </Card>
      {/* Add to Group */}
      {state.showAddToGroupModal && (
        <Modal className="plain-modal">
          <p>Select a group you would like to add {getUsersText()} to.</p>
          <GuiSelect
            label="Group"
            name="addToGroupId"
            options={groupOptions}
            value={state.addToGroupId}
            onChange={(name, option) => updateState(name, option.value)}
            onKeyDown={(e) => handleKeyDown(e)}
          />
          <div className="buttons">
            <GuiButton
              className="btn-outline"
              onClick={(e) =>
                updateState({
                  showAddToGroupModal: false,
                  addToGroupId: null,
                  individual: null,
                })
              }
            >
              Cancel
            </GuiButton>
            <GuiButton
              onClick={addUsersToGroup}
              disabled={!state.addToGroupId}
              loading={state.saving}
            >
              Add {state.individual ? "" : Object.keys(checkboxState).length}{" "}
              User
              {state.individual || Object.keys(checkboxState).length === 1
                ? ""
                : "s"}
            </GuiButton>
          </div>
        </Modal>
      )}

      {/* Delete */}
      {state.showDeleteUsersModal && (
        <Modal className="plain-modal">
          <p>
            Are you sure you want to permanently remove {getUsersText()} from
            the system?
          </p>
          <div className="buttons">
            <GuiButton
              className="btn-outline"
              onClick={(e) =>
                updateState({ showDeleteUsersModal: false, individual: null })
              }
            >
              Cancel
            </GuiButton>
            <GuiButton
              className="warning"
              onClick={deleteUsers}
              loading={state.saving}
            >
              Delete {state.individual ? "" : selectedRows.length} User
              {state.individual || selectedRows.length === 1 ? "" : "s"}
            </GuiButton>
          </div>
        </Modal>
      )}

      {/* High Priority */}
      {state.showMakeHighPriority && (
        <Modal className="plain-modal">
          <p>Would you like to make {getUsersText()} high priority?</p>
          <div className="buttons">
            <GuiButton
              className="btn-outline"
              onClick={(e) =>
                updateState({ showMakeHighPriority: false, individual: null })
              }
            >
              Cancel
            </GuiButton>
            <GuiButton
              onClick={(e) => setUserPriority(true)}
              loading={state.saving}
            >
              Make High Priority
            </GuiButton>
          </div>
        </Modal>
      )}

      {/* Low Priority */}
      {state.showRemoveHighPriority && (
        <Modal className="plain-modal">
          <p>
            Are you sure you want to remove high priority from {getUsersText()}?
          </p>
          <div className="buttons">
            <GuiButton
              className="btn-outline"
              onClick={(e) =>
                updateState({ showRemoveHighPriority: false, individual: null })
              }
            >
              Cancel
            </GuiButton>
            <GuiButton
              onClick={(e) => setUserPriority(false)}
              loading={state.saving}
            >
              Remove High Priority
            </GuiButton>
          </div>
        </Modal>
      )}
      {/* Make Leader */}
      {showModal.show && (
        <Modal>
          <p>Are you sure you want to make user Leader?</p>
          <GuiButton
            className="btn-outline"
            onClick={() => {
              setShowModal({ show: false, userId: null });
              handlePopoverHide(null);
            }}
          >
            Cancel
          </GuiButton>
          <GuiButton
            onClick={() => handleMakeLeader(showModal.userId)}
            loading={state.saving}
          >
            Make Leader
          </GuiButton>
        </Modal>
      )}
      {/* Remove */}
      {showRemoveModal.show && (
        <Modal className="plain-modal">
          <p>Are you sure you want to remove user from group?</p>

          <div className="modal-buttons">
            <GuiButton
              className="btn-outline"
              onClick={() => {
                setShowRemoveModal({ show: false, userId: null });
                handlePopoverHide(null);
              }}
            >
              Cancel
            </GuiButton>

            <GuiButton
              onClick={() => handleRemove(showRemoveModal.userId, groupId)}
              loading={state.saving}
            >
              Remove
            </GuiButton>
          </div>
        </Modal>
      )}
    </PageLayout>
  );
}
